import { getWeekColumns, getBucketForWeek, round2, round4 } from "./weeklyEACCalculations";

// Actual weeks are persisted/locked — they're shown exactly as the API returned them, no
// client-side calculation at all. Only Forecast weeks are recomputed live (so edited hours/
// unburden costs/COCR cascade), following the CLIN sheet's own formulas (Sweet Tooth EAC),
// which take precedence over the API doc's shape where the two disagree:
//
//   totalBurdenCost[i] = dlBurdenCostTotal[i] + odcBurdenCostTotal[i]
//   weeklyDelta[i]     = totalBurdenCost[i] + creditOverCeilingRate[i]
//   billableCosts[i]   = billableCosts[i-1] + totalBurdenCost[i]      // Credit excluded — sheet: =prev+TLD
//   reserveOnCosts[i]  = weeklyDelta[i] * 0.03                        // Credit included here
//   milestoneComponent[i] = (FFP) 0 : (CP/TM) fixedMonthlyMilestone[i]
//   cumulativeCostAndReserve[i] = cumulativeCostAndReserve[i-1] + weeklyDelta[i] + reserveOnCosts[i]
//                                 + milestoneComponent[i]             // Credit included here too
//   limitationOfFundingPercentage[i] = (FFP) cumulativeMilestone[i] / totalFundingValue
//                                     : (CP/TM) cumulativeCostAndReserve[i] / totalFundingValue
//   estimatedProfitPercent[i] = (CP/TM) cumulativeMilestone[i] / cumulativeCostAndReserve[i]  // a ratio
//                              : (FFP) (cumulativeMilestone[i] - (billableCosts[i] + cumulativeReserveOnCosts[i]))
//                                        / cumulativeMilestone[i]                              // a margin, credit excluded
//
// Credit for Over Ceiling Rate is now a stored CLIN-level value (cocrDetails[], one row per week
// for the whole CLIN, matched by weekStartDate — not per-employee, not computed) — editable the
// same way Hours is in Direct Labor. Fixed Monthly Milestone is a working-days spread of
// totalClinValue that's entirely independent of any editable value (hours/unburdenCost/cocrValue),
// so it's always taken from the persisted weeklyTotals rather than re-derived client-side — doing
// so would require a holiday calendar this API doesn't expose, and the persisted value never goes
// stale from anything the user can edit here. Total Labor Dollars is the combined burden cost of
// Direct Labor and Other Direct Costs (kept in sync with edits by recalculateBucketBurdenCost /
// recalculateOdcBucketBurdenCost).
export const computeWeeklyTotals = (
  employeeDetails = [],
  clinDetails = {},
  originalWeeklyTotals = [],
  odcDetails = [],
  cocrDetails = [],
  type,
) => {
  // The week list (with its Actual/Forecast type per week) is normally derived from Direct
  // Labor's own buckets, but a CLIN can have Other Direct Costs with no Direct Labor rows at all
  // (or vice versa) — fall back to whichever side actually has data, so the Summary table still
  // renders instead of going empty just because Direct Labor happens to be empty.
  const weeks = getWeekColumns(employeeDetails.length > 0 ? employeeDetails : odcDetails);
  const originalByWeek = {};
  originalWeeklyTotals.forEach((week) => {
    originalByWeek[week.weekStartDate] = week;
  });
  const cocrByWeek = {};
  cocrDetails.forEach((bucket) => {
    cocrByWeek[bucket.weekStartDate] = bucket;
  });

  const totalFundingValue = round2(Number(clinDetails.totalFundingValue) || 0);
  const totalClinValue = round2(Number(clinDetails.totalClinValue) || 0);

  let cumulativeBillableCosts = 0;
  let cumulativeCostAndReserve = 0;
  let cumulativeMilestone = 0;
  let cumulativeReserveOnCosts = 0;

  return weeks.map((week) => {
    const originalWeek = originalByWeek[week.weekStartDate];

    if (week.type === "Actual" && originalWeek) {
      // Keep the running trackers in sync with the API's own numbers so a later Forecast week
      // still cascades from the true persisted baseline.
      cumulativeBillableCosts = round2(Number(originalWeek.billableCosts) || 0);
      cumulativeCostAndReserve = round2(
        Number(originalWeek.cumulativeCostAndReserve) || 0,
      );
      cumulativeMilestone = round2(
        totalFundingValue - (Number(originalWeek.fundingRemainingBasedOnMilestones) || 0),
      );
      cumulativeReserveOnCosts = round2(
        cumulativeReserveOnCosts + round2(Number(originalWeek.reserveOnCosts) || 0),
      );

      return {
        weekStartDate: week.weekStartDate,
        weekEndDate: week.weekEndDate,
        type: week.type,
        totalLaborDollars: originalWeek.totalLaborDollars,
        creditOverCeilingRate: originalWeek.creditOverCeilingRate,
        billableCosts: originalWeek.billableCosts,
        reserveOnCosts: originalWeek.reserveOnCosts,
        cumulativeCostAndReserve: originalWeek.cumulativeCostAndReserve,
        fixedMonthlyMilestone: originalWeek.fixedMonthlyMilestone,
        estimatedProfitPercent: originalWeek.estimatedProfitPercent,
        fundingRemainingBasedOnMilestones:
          originalWeek.fundingRemainingBasedOnMilestones,
        fundingRemainingBasedOnCosts: originalWeek.fundingRemainingBasedOnCosts,
        limitationOfCeilingPercentage: originalWeek.limitationOfCeilingPercentage,
        limitationOfFundingPercentage: originalWeek.limitationOfFundingPercentage,
      };
    }

    let dlBurdenCostTotal = 0;
    employeeDetails.forEach((employee) => {
      const bucket = getBucketForWeek(employee, week.weekStartDate);
      if (!bucket) return;
      // Every individual value is rounded to 2 decimals before it takes part in any calculation
      // below, not just the combined result.
      dlBurdenCostTotal += round2(Number(bucket.burdenCost) || 0);
    });
    dlBurdenCostTotal = round2(dlBurdenCostTotal);

    let odcBurdenCostTotal = 0;
    odcDetails.forEach((odc) => {
      const bucket = getBucketForWeek(odc, week.weekStartDate);
      if (!bucket) return;
      odcBurdenCostTotal += round2(Number(bucket.burdenCost) || 0);
    });
    odcBurdenCostTotal = round2(odcBurdenCostTotal);

    // Credit for Over Ceiling Rate is a stored CLIN-level value for Forecast weeks (not
    // calculated), editable the same way Hours is in Direct Labor.
    const creditOverCeilingRate = round2(Number(cocrByWeek[week.weekStartDate]?.cocrValue) || 0);

    const totalBurdenCost = round2(dlBurdenCostTotal + odcBurdenCostTotal);
    const weeklyDelta = round2(totalBurdenCost + creditOverCeilingRate);
    const reserveOnCosts = round2(weeklyDelta * 0.03);

    // Billable Costs is a running total of burden cost only — Credit for Over Ceiling Rate is
    // NOT part of it (sheet: BillableCosts[i] = BillableCosts[i-1] + TotalLaborDollars[i]).
    cumulativeBillableCosts = round2(cumulativeBillableCosts + totalBurdenCost);

    // Fixed Monthly Milestone always comes from the API response — see the header comment above
    // for why this is never re-derived client-side.
    const fixedMonthlyMilestone = round2(Number(originalWeek?.fixedMonthlyMilestone) || 0);
    const milestoneComponent = type === "FFP" ? 0 : fixedMonthlyMilestone;

    cumulativeCostAndReserve = round2(
      cumulativeCostAndReserve + weeklyDelta + reserveOnCosts + milestoneComponent,
    );
    cumulativeMilestone = round2(cumulativeMilestone + fixedMonthlyMilestone);
    cumulativeReserveOnCosts = round2(cumulativeReserveOnCosts + reserveOnCosts);

    // CP/TM (sheet: cumFee/cumCost, a ratio) vs FFP (sheet: (cumFee-(cumBillableCosts+cumReserve))
    // /cumFee, a margin that excludes Credit for Over Ceiling from the cost side even though it's
    // part of Cumulative Cost and Reserve). Not multiplied by 100 (0.15 means 15%).
    const estimatedProfitPercent =
      type === "FFP"
        ? cumulativeMilestone === 0
          ? null
          : round4(
              (cumulativeMilestone - (cumulativeBillableCosts + cumulativeReserveOnCosts)) /
                cumulativeMilestone,
            )
        : cumulativeCostAndReserve === 0
          ? null
          : round4(cumulativeMilestone / cumulativeCostAndReserve);

    return {
      weekStartDate: week.weekStartDate,
      weekEndDate: week.weekEndDate,
      type: week.type,
      totalLaborDollars: totalBurdenCost,
      creditOverCeilingRate,
      billableCosts: cumulativeBillableCosts,
      reserveOnCosts,
      cumulativeCostAndReserve,
      fixedMonthlyMilestone,
      estimatedProfitPercent,
      fundingRemainingBasedOnMilestones: round2(totalFundingValue - cumulativeMilestone),
      fundingRemainingBasedOnCosts: round2(totalFundingValue - cumulativeCostAndReserve),
      limitationOfCeilingPercentage: totalClinValue
        ? round4(cumulativeCostAndReserve / totalClinValue)
        : null,
      // FFP tracks funding consumption against cumulative Milestone/Fee billed; CP/TM track it
      // against cumulative cost (sheet: CPFF row 47 uses cumCost, FFP row 61 uses cumFee).
      limitationOfFundingPercentage: totalFundingValue
        ? round4(
            (type === "FFP" ? cumulativeMilestone : cumulativeCostAndReserve) /
              totalFundingValue,
          )
        : null,
    };
  });
};
